import { useMutation } from '@urql/vue';

import type { CompletableType } from '~/graphql/generated/graphql';
import type { CompleteUserProgress, StartUserProgress } from '~/types/Learn/userProgress';

import { completeUserProgress, startUserProgress, userCoinsTransactionsFragment, userProgressFragment } from '~/graphql/documents/learn';
import { useFragment } from '~/graphql/generated';

export const useUserProgress = () => {
  const { isLoggedIn, user } = useAuth();

  const { executeMutation: _startUserProgress, fetching: fetchingStartUserProgress, stale: staleStartUserProgress } = useMutation(startUserProgress);

  const {
    executeMutation: _completeUserProgress,
    fetching: fetchingCompleteUserProgress,
    stale: staleCompleteUserProgress,
  } = useMutation(completeUserProgress);

  const currentCoinBalance = computed(() => user.value?.latestCoinBalance || 0);

  const startProgress = async (completableId: string, completableType: CompletableType): Promise<StartUserProgress> => {
    if (!isLoggedIn.value) {
      return { userProgress: null, coinTransaction: null };
    }

    const { error, data } = await _startUserProgress({
      input: {
        completableId,
        completableType,
      },
    });

    if (error) {
      return { error };
    }

    const userProgress = useFragment(userProgressFragment, data?.startUserProgress.userProgress) || null;
    const coinTransaction = useFragment(userCoinsTransactionsFragment, userProgress?.userCoinsTransaction) || null;
    return {
      userProgress,
      coinTransaction,
      fetchingStartUserProgress,
      staleStartUserProgress,
    };
  };

  const completeProgress = async (progressId: string, skillSlugs?: string[]): Promise<CompleteUserProgress> => {
    if (!isLoggedIn.value) {
      return { userProgress: null, coinTransaction: null };
    }

    const { error, data } = await _completeUserProgress({
      input: {
        userProgressId: progressId,
        ...(skillSlugs?.length ? { skillSlugs } : {}),
      },
    });

    if (error) {
      return { error };
    }

    const userProgress = useFragment(userProgressFragment, data?.completeUserProgress.userProgress) || null;
    const coinTransaction = useFragment(userCoinsTransactionsFragment, data?.completeUserProgress.earnedCoinTransaction) || null;
    return {
      userProgress,
      coinTransaction,
      fetchingCompleteUserProgress,
      staleCompleteUserProgress,
    };
  };

  return {
    startProgress,
    completeProgress,
    currentCoinBalance,
  };
};
